import { FC } from 'react';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { DrawerPage } from '../../components/CareMapDrawerPage';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { IonButton } from '@ionic/react';
import { useSubscriptionV2 } from '../../support/state/useSubscriptionV2';
import { ActiveCaremapNoteEditor } from './ActiveCaremapNoteEditor';
import { AlertPresenter } from '../../AlertBinder';

interface IEditNodeDrawerProps {
    noteId: string;
    hasPrivateCheckbox?: boolean;
};

export class EditNoteWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IEditNodeDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IEditNodeDrawerProps>){}

    mode = 'edit-note';

    Component = EditNoteDrawerView;

    open = (props: IEditNodeDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };
};

export const EditNoteDrawerView: FC<IEditNodeDrawerProps> = ({ noteId, hasPrivateCheckbox }) => {
    const alert = useInstance(AlertPresenter);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const noteVM = useSubscriptionV2(async () => await notesFactory.createEditForm(noteId));

    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const saveAndClose = async () => {
        if (!noteVM){
            return;
        }
        try {
            await noteVM.save();
            drawerHelper.close();
        }
        catch (err: any){
            alert.showAndLogError(err);
        }
    };

    if (!noteVM){
        return null;
    }

    const activeCareMap = careMapState.getActiveCaremap();
    if (!activeCareMap){
        return null;
    }

    let title = 'Edit Note';

    const canEdit = careMapState.hasPermissionTo('put-note');
    if (!canEdit){
        title = 'View Note';
    }

    return (
        <DrawerPage
            title={title}
            hasBack={false}
            data-type="note-editor"
            onClose={!canEdit ? drawerHelper.close : undefined}
            buttons={canEdit && (
                <>
                    <IonButton
                        data-type="save-button"
                        disabled={!noteVM.canSave()}
                        onClick={saveAndClose}>Save</IonButton>
                    <IonButton data-type="cancel-button" fill="outline" onClick={() => {
                        drawerHelper.close();
                    }}>Cancel</IonButton>
                </>
            )}
        >
            <ActiveCaremapNoteEditor
                noteVM={noteVM}
                onDeleted={drawerHelper.close}
                hasPrivateCheckbox={hasPrivateCheckbox}
            />
        </DrawerPage>
    );
};

